import React, { useEffect, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { yaml } from '@codemirror/lang-yaml';
import type { Extension } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';

const yamlBasicSetup = {
  lineNumbers: true,
  foldGutter: true,
  highlightActiveLine: true,
};

/**
 * CodeMirror reads `value` only at mount (via `editorKey`).
 * Prevents @uiw/react-codemirror from replacing the doc from a stale controlled `value`.
 */
export function DraftYamlEditorPane({
  editorKey,
  initialText,
  height = '520px',
  extensions = [yaml()],
  readOnly = false,
  onChange,
  onCreateEditor,
  registerGetText,
}: {
  editorKey: number;
  initialText: string;
  height?: string;
  extensions?: Extension[];
  readOnly?: boolean;
  onChange: (value: string) => void;
  onCreateEditor?: (view: EditorView) => void;
  /** Parent reads live doc text before tab switches (editor still mounted). */
  registerGetText?: (getText: (() => string) | null) => void;
}) {
  const viewRef = useRef<EditorView | null>(null);

  useEffect(() => {
    if (!registerGetText) return;
    registerGetText(() => viewRef.current?.state.doc.toString() ?? initialText);
    return () => registerGetText(null);
  }, [editorKey, initialText, registerGetText]);

  return (
    <CodeMirror
      key={editorKey}
      value={initialText}
      height={height}
      editable={!readOnly}
      basicSetup={yamlBasicSetup}
      extensions={extensions}
      onChange={onChange}
      onCreateEditor={(view) => {
        viewRef.current = view;
        onCreateEditor?.(view);
      }}
    />
  );
}
